import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Slider } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { MenuButton } from 'components/buttons';
import { AssignmentFilter } from 'components/reports/filters/AssignmentFilter';
import { CourseFilter } from 'components/reports/filters/CourseFilter';
import { GroupFilter } from 'components/reports/filters/GroupFilter';
import { Color } from 'core';
import { ExportToCsv } from 'export-to-csv';
import { useAssignments } from 'hooks/useAssignments';
import { useCourse } from 'hooks/useCourse';
import { getLearnerStats } from 'hooks/useLearnerStats';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import styled from 'styled-components';
import { TAssignment } from 'types';
import convertProgressToInteger from 'utils/convertProgressToInteger';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';
import { transformMilliseconds } from 'utils/timeUtils';
import { AnalyticsService } from 'utils/AnalyticsService';
import { useReportStore } from 'zstore';

// Quadrant colors
const QUADRANT_COLORS = {
  highProgressHighReadiness: Color.effectivenessHighProgressHighReadiness, // Green - "Retaining Champions"
  highProgressLowReadiness: Color.effectivenessHighProgressLowReadiness, // Orange - "Completed but Fading"
  lowProgressHighReadiness: Color.effectivenessLowProgressHighReadiness, // Blue - "Early Promise"
  lowProgressLowReadiness: Color.effectivenessLowProgressLowReadiness, // Gray - "Needs Attention"
};

interface LearnerDataPoint {
  id: string;
  name: string;
  email: string;
  progress: number;
  readiness: number;
  lastStudyTime: string | null;
  totalStudyTimeMillis: number;
  quadrant: string;
}

interface QuadrantStats {
  highProgressHighReadiness: number;
  highProgressLowReadiness: number;
  lowProgressHighReadiness: number;
  lowProgressLowReadiness: number;
}

const getQuadrantForThresholds = (
  progress: number,
  readiness: number,
  progressThreshold: number,
  readinessThreshold: number
): string => {
  if (progress >= progressThreshold && readiness >= readinessThreshold) {
    return 'highProgressHighReadiness';
  } else if (progress >= progressThreshold && readiness < readinessThreshold) {
    return 'highProgressLowReadiness';
  } else if (progress < progressThreshold && readiness >= readinessThreshold) {
    return 'lowProgressHighReadiness';
  } else {
    return 'lowProgressLowReadiness';
  }
};

export const EffectivenessMap = () => {
  return (
    <>
      <Filters />
      <EducationPanel />
      <Data />
    </>
  );
};

const Filters = () => {
  return (
    <div>
      <CourseFilter />
      <AssignmentFilter includeAll={true} />
      <GroupFilter />
    </div>
  );
};

const EducationPanel = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <EducationContainer>
      <EducationHeader onClick={() => setExpanded(!expanded)}>
        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px', color: Color.primaryBlue }} />
        <span style={{ fontWeight: 600 }}>{t('Understanding Progress vs Knowledge Retention')}</span>
        <ExpandIcon expanded={expanded}>{expanded ? '−' : '+'}</ExpandIcon>
      </EducationHeader>
      {expanded && (
        <EducationContent>
          <EducationRow>
            <EducationColumn>
              <EducationTitle>{t('Progress')}</EducationTitle>
              <EducationText>
                {t(
                  'Measures how much content a learner has completed toward their goal level. Progress does not decay over time—once content is studied, progress remains.'
                )}
              </EducationText>
            </EducationColumn>
            <EducationColumn>
              <EducationTitle>{t('Knowledge Retention')}</EducationTitle>
              <EducationText>
                {t(
                  'Predicts how much knowledge a learner can recall right now. Knowledge retention naturally decays over time without review, reflecting how memory works.'
                )}
              </EducationText>
            </EducationColumn>
          </EducationRow>
          <StakeholderTip>
            <strong>{t('For stakeholders:')}</strong>{' '}
            {t(
              'High progress with declining knowledge retention indicates learners who completed training but may need refreshers. This is the "forgetting curve" in action—and why Cerego\'s spaced repetition matters.'
            )}
          </StakeholderTip>
        </EducationContent>
      )}
    </EducationContainer>
  );
};

const Data = () => {
  const { t } = useTranslation();
  const courseId = useReportStore((state) => state.courseId);
  const assignmentId = useReportStore((state) => state.assignmentId);
  const group = useReportStore((state) => state.group);

  const { data: course } = useCourse(courseId || '');
  const { data: assignments } = useAssignments(courseId);
  const assignment = assignments?.find((a: TAssignment) => a.id === assignmentId);

  const [learnerData, setLearnerData] = useState<LearnerDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [progressThreshold, setProgressThreshold] = useState(80);
  const [readinessThreshold, setReadinessThreshold] = useState(80);
  const latestLearnerFetchRequestId = useRef(0);
  const progressThresholdRef = useRef(progressThreshold);
  const readinessThresholdRef = useRef(readinessThreshold);

  // Check if assignment is an assessment (no readiness)
  const isAssessment = assignment?.goalType === 'assessment';

  useEffect(() => {
    progressThresholdRef.current = progressThreshold;
    readinessThresholdRef.current = readinessThreshold;
  }, [progressThreshold, readinessThreshold]);

  // Fetch all learner data when filters change
  useEffect(() => {
    if (courseId) {
      const requestId = ++latestLearnerFetchRequestId.current;
      setIsLoading(true);

      const fetchAllLearnerData = async () => {
        try {
          const allData = await getAllLearnerStats(courseId, assignmentId || '', group);
          if (requestId !== latestLearnerFetchRequestId.current) return;
          const processedData = allData
            .filter((stat) => stat.readiness !== undefined && stat.readiness !== null)
            .map((stat) => {
              const progress = convertProgressToInteger(stat.progress);
              const readiness = Math.round(stat.readiness * 100);

              return {
                id: stat.user.id,
                name: stat.user.name,
                email: stat.user.email,
                progress,
                readiness,
                lastStudyTime: stat.lastStudyTime,
                totalStudyTimeMillis: stat.totalStudyTimeMillis,
                quadrant: getQuadrantForThresholds(
                  progress,
                  readiness,
                  progressThresholdRef.current,
                  readinessThresholdRef.current
                ),
              };
            });
          setLearnerData(processedData);
        } catch (error) {
          if (requestId !== latestLearnerFetchRequestId.current) return;
          console.error('Failed to fetch learner data:', error);
          setLearnerData([]);
        } finally {
          if (requestId === latestLearnerFetchRequestId.current) {
            setIsLoading(false);
          }
        }
      };

      fetchAllLearnerData();
    } else {
      latestLearnerFetchRequestId.current += 1;
      setLearnerData([]);
      setIsLoading(false);
    }
  }, [courseId, assignmentId, group]);

  const getAllLearnerStats = async (courseId: string, setId: string, groupFilter: string | null): Promise<any[]> => {
    let learnerStats: any[] = [];
    const pageSize = 200;

    const res = await getLearnerStats('key', courseId, '', 'asc', 1, pageSize, '', groupFilter ?? '', setId);
    if (!res) {
      throw new Error('Failed to fetch learner stats.');
    }

    const totalPages = res.data.meta['total-pages'];
    const resData: any = normalizeJSONAPIResponse(res.data);
    learnerStats.push(...resData);

    for (let i = 2; i <= totalPages; i++) {
      const res = await getLearnerStats('key', courseId, '', 'asc', i, pageSize, '', groupFilter ?? '', setId);
      if (!res) {
        throw new Error('Failed to fetch learner stats.');
      }
      const resData: any = normalizeJSONAPIResponse(res.data);
      learnerStats.push(...resData);
    }

    return learnerStats;
  };

  const getQuadrant = useCallback(
    (progress: number, readiness: number): string =>
      getQuadrantForThresholds(progress, readiness, progressThreshold, readinessThreshold),
    [progressThreshold, readinessThreshold]
  );

  // Update quadrants when thresholds change
  useEffect(() => {
    setLearnerData((prev) => {
      if (prev.length === 0) return prev;

      const next = prev.map((d) => ({
        ...d,
        quadrant: getQuadrant(d.progress, d.readiness),
      }));
      const hasChanged = next.some((learner, index) => learner.quadrant !== prev[index].quadrant);

      return hasChanged ? next : prev;
    });
  }, [getQuadrant]);

  const calculateQuadrantStats = (): QuadrantStats => {
    return learnerData.reduce(
      (acc, learner) => {
        acc[learner.quadrant as keyof QuadrantStats]++;
        return acc;
      },
      {
        highProgressHighReadiness: 0,
        highProgressLowReadiness: 0,
        lowProgressHighReadiness: 0,
        lowProgressLowReadiness: 0,
      }
    );
  };

  const calculateSummaryStats = () => {
    if (learnerData.length === 0) {
      return { avgProgress: 0, avgReadiness: 0, totalLearners: 0 };
    }

    const avgProgress = Math.round(learnerData.reduce((sum, l) => sum + l.progress, 0) / learnerData.length);
    const avgReadiness = Math.round(learnerData.reduce((sum, l) => sum + l.readiness, 0) / learnerData.length);

    return {
      avgProgress,
      avgReadiness,
      totalLearners: learnerData.length,
    };
  };

  const handleCsvDownload = async () => {
    AnalyticsService.getInstance().track('button_clicked', { button_type: 'Download effectiveness map' });
    setIsDownloading(true);

    const csvData = learnerData.map((learner) => ({
      [t('Course')]: course?.name || 'N/A',
      [t('Assignment')]: assignment?.name || t('All Assignments'),
      [t('Group')]: group || t('All Groups'),
      [t('Name')]: learner.name,
      [t('Email')]: learner.email,
      [t('Progress')]: `${learner.progress}%`,
      [t('Knowledge Retention')]: `${learner.readiness}%`,
      [t('Quadrant')]: getQuadrantLabel(learner.quadrant),
      [t('Last Visit')]: learner.lastStudyTime ? moment(learner.lastStudyTime).format('YYYY-MM-DD HH:mm:ss') : '',
      [t('Total Study Time')]: transformMilliseconds(learner.totalStudyTimeMillis, 'total'),
    }));

    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      filename: `effectiveness-map-${new Date().toISOString()}`,
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(csvData);
    setIsDownloading(false);
  };

  const getQuadrantLabel = (quadrant: string): string => {
    switch (quadrant) {
      case 'highProgressHighReadiness':
        return t('Retaining Champions');
      case 'highProgressLowReadiness':
        return t('Completed but Fading');
      case 'lowProgressHighReadiness':
        return t('Early Promise');
      case 'lowProgressLowReadiness':
        return t('Needs Attention');
      default:
        return '';
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as LearnerDataPoint;
      return (
        <TooltipContainer>
          <TooltipName>{data.name}</TooltipName>
          <TooltipEmail>{data.email}</TooltipEmail>
          <TooltipRow>
            <span>{t('Progress')}:</span>
            <span>{data.progress}%</span>
          </TooltipRow>
          <TooltipRow>
            <span>{t('Knowledge Retention')}:</span>
            <span>{data.readiness}%</span>
          </TooltipRow>
          {data.lastStudyTime && (
            <TooltipRow>
              <span>{t('Last Visit')}:</span>
              <span>{moment(data.lastStudyTime).fromNow()}</span>
            </TooltipRow>
          )}
          <TooltipRow>
            <span>{t('Total Time')}:</span>
            <span>{transformMilliseconds(data.totalStudyTimeMillis, 'total')}</span>
          </TooltipRow>
          <TooltipQuadrant style={{ color: QUADRANT_COLORS[data.quadrant as keyof typeof QUADRANT_COLORS] }}>
            {getQuadrantLabel(data.quadrant)}
          </TooltipQuadrant>
        </TooltipContainer>
      );
    }
    return null;
  };

  // Show loading state
  if (isLoading) {
    return <Skeleton variant="rect" width="100%" height={500} />;
  }

  // Show message if no course selected
  if (!courseId) {
    return (
      <EmptyState>
        <p>{t('Select a course to view the effectiveness map.')}</p>
      </EmptyState>
    );
  }

  // Show message for assessments (no readiness data)
  if (isAssessment) {
    return (
      <EmptyState>
        <p>
          {t(
            'Knowledge retention data is not available for assessment-type assignments. Please select a different assignment or view all assignments.'
          )}
        </p>
      </EmptyState>
    );
  }

  // Show message if no data
  if (learnerData.length === 0) {
    return (
      <EmptyState>
        <p>{t('No learner data found for the selected filters.')}</p>
      </EmptyState>
    );
  }

  const quadrantStats = calculateQuadrantStats();
  const summaryStats = calculateSummaryStats();

  return (
    <MapContainer>
      {/* Summary Stats */}
      <SummarySection>
        <SummaryCard>
          <SummaryValue>{summaryStats.totalLearners}</SummaryValue>
          <SummaryLabel>{t('Total Learners')}</SummaryLabel>
        </SummaryCard>
        <SummaryCard>
          <SummaryValue>{summaryStats.avgProgress}%</SummaryValue>
          <SummaryLabel>{t('Avg Progress')}</SummaryLabel>
        </SummaryCard>
        <SummaryCard>
          <SummaryValue>{summaryStats.avgReadiness}%</SummaryValue>
          <SummaryLabel>{t('Avg Knowledge Retention')}</SummaryLabel>
        </SummaryCard>
      </SummarySection>

      {/* Threshold Controls */}
      <ThresholdSection>
        <ThresholdControl>
          <ThresholdLabel>
            {t('Progress Threshold')}: {progressThreshold}%
          </ThresholdLabel>
          <Slider
            value={progressThreshold}
            onChange={(_, value) => setProgressThreshold(value as number)}
            min={0}
            max={100}
            step={5}
            style={{ width: 200 }}
          />
        </ThresholdControl>
        <ThresholdControl>
          <ThresholdLabel>
            {t('Knowledge Retention Threshold')}: {readinessThreshold}%
          </ThresholdLabel>
          <Slider
            value={readinessThreshold}
            onChange={(_, value) => setReadinessThreshold(value as number)}
            min={0}
            max={100}
            step={5}
            style={{ width: 200 }}
          />
        </ThresholdControl>
      </ThresholdSection>

      {/* Quadrant Legend */}
      <QuadrantLegend>
        <LegendItem>
          <LegendDot style={{ backgroundColor: QUADRANT_COLORS.highProgressHighReadiness }} />
          <LegendText>
            {t('Retaining Champions')} ({quadrantStats.highProgressHighReadiness})
          </LegendText>
        </LegendItem>
        <LegendItem>
          <LegendDot style={{ backgroundColor: QUADRANT_COLORS.highProgressLowReadiness }} />
          <LegendText>
            {t('Completed but Fading')} ({quadrantStats.highProgressLowReadiness})
          </LegendText>
        </LegendItem>
        <LegendItem>
          <LegendDot style={{ backgroundColor: QUADRANT_COLORS.lowProgressHighReadiness }} />
          <LegendText>
            {t('Early Promise')} ({quadrantStats.lowProgressHighReadiness})
          </LegendText>
        </LegendItem>
        <LegendItem>
          <LegendDot style={{ backgroundColor: QUADRANT_COLORS.lowProgressLowReadiness }} />
          <LegendText>
            {t('Needs Attention')} ({quadrantStats.lowProgressLowReadiness})
          </LegendText>
        </LegendItem>
      </QuadrantLegend>

      {/* Scatter Chart */}
      <ChartContainer>
        <ResponsiveContainer width="100%" height={500}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="progress"
              name="Progress"
              domain={[0, 100]}
              label={{ value: t('Progress (%)'), position: 'bottom', offset: 40 }}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis
              type="number"
              dataKey="readiness"
              name="Knowledge Retention"
              domain={[0, 100]}
              label={{ value: t('Knowledge Retention (%)'), angle: -90, position: 'insideLeft', offset: -10 }}
              tickFormatter={(value) => `${value}%`}
            />
            <ZAxis range={[60, 60]} />
            <RechartsTooltip content={<CustomTooltip />} />

            {/* Reference lines for thresholds */}
            <ReferenceLine x={progressThreshold} stroke={Color.textGray} strokeDasharray="5 5" />
            <ReferenceLine y={readinessThreshold} stroke={Color.textGray} strokeDasharray="5 5" />

            {/* Quadrant background shading */}
            <ReferenceArea
              x1={progressThreshold}
              x2={100}
              y1={readinessThreshold}
              y2={100}
              fill={QUADRANT_COLORS.highProgressHighReadiness}
              fillOpacity={0.1}
            />
            <ReferenceArea
              x1={progressThreshold}
              x2={100}
              y1={0}
              y2={readinessThreshold}
              fill={QUADRANT_COLORS.highProgressLowReadiness}
              fillOpacity={0.1}
            />
            <ReferenceArea
              x1={0}
              x2={progressThreshold}
              y1={readinessThreshold}
              y2={100}
              fill={QUADRANT_COLORS.lowProgressHighReadiness}
              fillOpacity={0.1}
            />
            <ReferenceArea
              x1={0}
              x2={progressThreshold}
              y1={0}
              y2={readinessThreshold}
              fill={QUADRANT_COLORS.lowProgressLowReadiness}
              fillOpacity={0.1}
            />

            <Scatter name="Learners" data={learnerData}>
              {learnerData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={QUADRANT_COLORS[entry.quadrant as keyof typeof QUADRANT_COLORS]} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </ChartContainer>

      {/* Download Button */}
      <DownloadSection>
        <MenuButton pending={isDownloading} text={t('Download CSV')} click={handleCsvDownload} hideArrow={true} />
      </DownloadSection>
    </MapContainer>
  );
};

// Styled Components
const MapContainer = styled.div`
  padding: 20px;
`;

const EducationContainer = styled.div`
  margin: 20px;
  border: 1px solid ${Color.lightGray};
  border-radius: 8px;
  background-color: ${Color.white};
`;

const EducationHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: ${Color.grayTransparent};
  }
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  margin-left: auto;
  font-size: 20px;
  font-weight: bold;
  color: ${Color.primaryBlue};
`;

const EducationContent = styled.div`
  padding: 0 16px 16px 16px;
  border-top: 1px solid ${Color.lightGray};
`;

const EducationRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EducationColumn = styled.div``;

const EducationTitle = styled.h4`
  margin: 0 0 8px 0;
  color: ${Color.primaryBlack};
`;

const EducationText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${Color.textGray};
  line-height: 1.5;
`;

const StakeholderTip = styled.div`
  margin-top: 16px;
  padding: 12px;
  background-color: ${Color.grayTransparent};
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
`;

const SummarySection = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SummaryCard = styled.div`
  background-color: ${Color.white};
  border: 1px solid ${Color.lightGray};
  border-radius: 8px;
  padding: 20px 30px;
  text-align: center;
  min-width: 150px;
`;

const SummaryValue = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: ${Color.primaryBlack};
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: ${Color.textGray};
  margin-top: 4px;
`;

const ThresholdSection = styled.div`
  display: flex;
  gap: 40px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const ThresholdControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ThresholdLabel = styled.span`
  font-size: 14px;
  color: ${Color.textGray};
`;

const QuadrantLegend = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
`;

const LegendText = styled.span`
  font-size: 14px;
  color: ${Color.textGray};
`;

const ChartContainer = styled.div`
  background-color: ${Color.white};
  border: 1px solid ${Color.lightGray};
  border-radius: 8px;
  padding: 20px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  margin: 20px;
  background-color: ${Color.white};
  border: 1px solid ${Color.lightGray};
  border-radius: 8px;
  color: ${Color.textGray};
`;

const DownloadSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const TooltipContainer = styled.div`
  background-color: ${Color.white};
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const TooltipName = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`;

const TooltipEmail = styled.div`
  font-size: 12px;
  color: ${Color.textGray};
  margin-bottom: 8px;
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  margin-bottom: 4px;
`;

const TooltipQuadrant = styled.div`
  font-weight: 600;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${Color.lightGray};
`;
